import { use, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Carousel, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams } from 'react-router';
import LotCountContext from '../../Contexts/LotCountContext';
import { Dictionary } from '../../Types/Dictionary';
import { Lot } from '../../Types/Lot';
import { useGetLotDetails } from '../../Utilities/useLotDetails';
import { LotDetailsSelector } from './LotDetailsSelector';
import LotDetailPageWrapper from './LotDetailsPageWrapper';
import LotErrorPage from './LotErrorPage';
import { ErrorResponse, isErrorResponse } from '../../Types/ErrorResponse';

export function LotDetailsPage() {
    const { t } = useTranslation();
    const { lotNumber: initialLotNumber } = useParams<{ lotNumber: string; }>();
    const { lotList, setLotList } = use(LotCountContext);
    const [activeLotIndex, setActiveLotIndex] = useState<number>(lotList.findIndex(x => x === initialLotNumber));

    const [currentLotError, setCurrentLotError] = useState<ErrorResponse | null>(null);
    
    const [loadedCurrentLot, setLoadedCurrentLot] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    // UseEffect to catch forward and back navigation. 
    // We've lied to react router to allow us to have smooth page transitions so we can't rely on react router's params hook to track lot numbers 
    useEffect(() => {
        const listener = window.addEventListener("popstate", (event) => {
            const pathName = window.location.pathname;
            if (!pathName.startsWith("/lotdetails")) {
                return;
            }

            const parts = pathName.split("/");
            if (parts.length !== 3) {
                return;
            }

            const lotNumber = parts[2];
            setActiveLotIndex(lotList.findIndex(x => x === lotNumber));
        });

        return listener;
    }, [lotList]);

    const { getLotDetails } = useGetLotDetails();

    const [preloadedLots, setPreloadedLots] = useState<Dictionary<Lot>>({});

    const fetchCurrentLot = useCallback(async (lotNumber: string) => {
        // check if the lot is already preloaded
        if (preloadedLots[lotNumber]) {
            console.log(`Not getting lot details for ${lotNumber} - we already have them`)

            setLoadedCurrentLot(true);
            setCurrentLotError(null);

            return;
        }

        setLoading(true);

        const response = await getLotDetails(lotNumber);

        if (!isErrorResponse(response)) {
            setLoadedCurrentLot(true);

            setCurrentLotError(null);

            var newLoaded = {
                ...preloadedLots,
                [lotNumber]: response
            };

            setPreloadedLots(newLoaded);
        } else {
            setCurrentLotError(response);
        }

        setLoading(false);

    }, [getLotDetails, preloadedLots]);

    const fetchNeighbourLots = useCallback(async (selectedIndex: number) => {

        const promises: { lotNumber: string; promise: Promise<Lot | ErrorResponse> }[] = [];

        if (lotList.length > 0) {
            if (selectedIndex !== 0) {
                let previousLotNumber = lotList[selectedIndex - 1];
                if (preloadedLots[previousLotNumber] === undefined) {
                    promises.push({
                        lotNumber: previousLotNumber,
                        promise: getLotDetails(previousLotNumber)
                    });
                }
            }

            if (selectedIndex !== lotList.length - 1) {
                let nextLotNumber = lotList[selectedIndex + 1];
                if (preloadedLots[nextLotNumber] === undefined) {
                    promises.push({
                        lotNumber: nextLotNumber,
                        promise: getLotDetails(nextLotNumber)
                    });
                }
            }
        }

        if (promises.length > 0) {
            setLoading(true);

            Promise.all(promises.map(x => x.promise))
                .then((results) => {
                    setPreloadedLots(prev => {
                        const updated = { ...prev };

                        results.forEach((result, index) => {
                            if (isErrorResponse(result)) {
                                setLotList(prev =>
                                    prev.filter(x => x !== promises[index].lotNumber)
                                );
                            } else {
                                updated[result.summary.lotNumber] = result;
                            }
                        });

                        return updated;
                    });
                })
                .finally(() => setLoading(false));
        }

    }, [lotList, preloadedLots, getLotDetails, setLotList]);


    useEffect(() => {

        setLoadedCurrentLot(false);

        const currentLotNumber = activeLotIndex === -1
                ? initialLotNumber ?? ""
                : lotList[activeLotIndex];

        if (!currentLotNumber) {
            return;
        }

        fetchCurrentLot(currentLotNumber);

    }, [activeLotIndex, initialLotNumber, lotList, fetchCurrentLot, fetchNeighbourLots]);

    useEffect(() => {
        if (loadedCurrentLot) {
            fetchNeighbourLots(activeLotIndex);
        }
    }, [activeLotIndex, loadedCurrentLot]);

    const handleSlideChange = (selectedIndex: number) => {
        // Alter the URL without triggering a proper react-router event
        const newUrl = `/lotdetails/${lotList[selectedIndex]}`;
        window.history.pushState({}, "", newUrl);
        setActiveLotIndex(selectedIndex);
    };

    /**
    * Called when a lot is renamed to update the dictionary with the new lot number
    * Removes the old lot number entry and adds the new one
    */
    const handleLotRenamed = useCallback((oldLotNumber: string, newLotNumber: string, updatedLot: Lot) => {
        setLotList((prev) => prev.map(lotNumber => lotNumber === oldLotNumber ? newLotNumber : lotNumber));
        setPreloadedLots((prev) => {
            const updated = { ...prev };
            updated[newLotNumber] = updatedLot;

            delete updated[oldLotNumber];
            return updated;
        });
    }, [setLotList]);

    const currentLot = preloadedLots[initialLotNumber ?? ""];

    if (!loading) {
        if (currentLotError?.statusCode === 404) {
            return <LotErrorPage title={t('LotDetails.ErrorPage.LotNotFound.Title', { lotNumber: initialLotNumber })} />;
        }

        if (currentLotError?.statusCode === 500) {
            return <LotErrorPage />;
        }
    }

    return (
        <Container fluid>
            {lotList.length === 0 // We have an empty lot list page on page refreshes so need to handle this case
                ? <LotDetailPageWrapper initialLot={currentLot} active={true}>
                    <LotDetailsSelector businessUnitCode={currentLot?.summary?.businessUnitCode} lotNumber={initialLotNumber ?? ""} />
                </LotDetailPageWrapper>
                : <>
                    <Carousel
                        activeIndex={activeLotIndex}
                        onSelect={handleSlideChange}
                        interval={null}
                        indicators={false}
                        controls={false}
                        wrap={false}
                    >
                        {lotList.map((lotNumber, index) =>
                            <Carousel.Item key={`${index}-slide`}>
                                <LotDetailPageWrapper initialLot={preloadedLots[lotNumber]} active={index === activeLotIndex} onLotRenamed={handleLotRenamed}>
                                    <LotDetailsSelector businessUnitCode={preloadedLots[lotNumber]?.summary?.businessUnitCode} lotNumber={lotNumber} />
                                </LotDetailPageWrapper>
                            </Carousel.Item>
                        )}
                    </Carousel>
                    {activeLotIndex !== 0 && (
                        <OverlayTrigger
                            placement="right"
                            overlay={(props) => (
                                <Tooltip id="button-tooltip" {...props}>
                                    {lotList[activeLotIndex - 1]}
                                </Tooltip>
                            )}
                        >
                            <Button className={`m-2 position-fixed top-50 start-0`} onClick={() => handleSlideChange(activeLotIndex - 1)}>
                                <FontAwesomeIcon icon={faChevronLeft} size={"xl"} />
                            </Button>
                        </OverlayTrigger>
                    )}
                    {activeLotIndex !== lotList.length - 1 && (
                        <OverlayTrigger
                            placement="left"
                            overlay={(props) => (
                                <Tooltip id="button-tooltip" {...props}>
                                    {lotList[activeLotIndex + 1]}
                                </Tooltip>
                            )}
                        >
                            <Button className={`m-2 position-fixed top-50 end-0`} onClick={() => handleSlideChange(activeLotIndex + 1)}>
                                <FontAwesomeIcon icon={faChevronRight} size={"xl"} />
                            </Button>
                        </OverlayTrigger>
                    )}
                </>
            }
        </Container>
    );
}

export default LotDetailsPage;